/* eslint-disable quotes */
"use client"

import styled from "@emotion/styled"
import { yupResolver } from "@hookform/resolvers/yup"
import {
  Box,
  Button,
  Checkbox,
  FilledInput,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  Modal,
} from "@mui/material"
import { useMutation } from "@tanstack/react-query"
import { useTranslations } from "next-intl"
import Image from "next/image"
import { useRouter } from "next/navigation"
import { LegacyRef, useEffect, useMemo, useRef, useState } from "react"
import ReCAPTCHA from "react-google-recaptcha"
import { Controller, SubmitHandler, useForm } from "react-hook-form"
import {
  CheckboxIcon,
  CheckedCheckboxIcon,
  CheckMark2Icon,
  Desktop,
  Mobile,
  NewText,
  SuccessFailJob,
} from "ui"
import { sharedColors as colors, sharedColors } from "ui/theme/colors"
import {
  jobFormClick,
  jobFormErrorClick,
  jobFormSuccessClick,
  postApply,
  schemaCommon,
} from "utils"
import * as yup from "yup"
import { ctaMessage } from "../Contact/ContactForm"
import FileUpload from "./FileUpload"
import UploadProgress from "./UploadProgress"

// eslint-disable-next-line prefer-destructuring
const RECAPTCHA_KEY = process.env.RECAPTCHA_KEY
if (!RECAPTCHA_KEY) {
  throw new Error("Please provide the RECAPTCHA_KEY environment variable.")
}

const StyledForm = styled.form`
  width: 100%;
  min-height: 457px;
  border-radius: 16px;
  background: #fff;
  overflow: var(--form-overflow, hidden);
  display: flex;
  flex-direction: column;
  justify-content: center;
  font-family: var(--narkis);
  padding-bottom: var(--form-padding-bottom, 0);
  position: var(--form-position, relative);

  ${(props) => props.theme.breakpoints.up("md")} {
    padding-inline: var(--form-padding-desktop, 24px);
    height: fit-content;
    display: block;
  }
  input {
    font-size: 16px;
  }
  label {
    font-size: 16px;
    color: #6F6F6F;
  }
`
const StyledHelperText = styled(FormHelperText, {
  shouldForwardProp: (prop) => prop !== "status",
})<{ status: boolean }>`
  margin-top: 4px;
  margin-bottom: 0;
  position: static;
  color: ${(props) => (props.status ? "#008015" : "#C92418")};
  font-size: 12px;
  height: 20px;
`
const StyledTextResponse = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`

const StyledInsideForm = styled.div<{ $withBackground: boolean }>`
  padding: ${(p) => (p.$withBackground ? "32px 16px" : "var(--form-inside-padding-mobile, 16px)")};
  margin-bottom: 16px;
  height: 100%;
  .MuiFilledInput-root {
    height: 56px;
    border: 1px solid #000000;
    border-radius: 4px;
    outline: none !important;
    input {
      padding-left: 8px;
      padding-right: 8px;
    }
    &::before,
    &::after {
      display: none;
    }
  }
  .MuiInputLabel-root {
    transform: translate(8px, 16px) scale(1);
  }
  .MuiInputLabel-shrink {
    transform: translate(8px, 7px) scale(0.75);
  }
  background-color: #F7F7F7;
  ${(p) => p.theme.breakpoints.up("md")} {
    padding: ${(p) =>
      p.$withBackground ? "32px" : "var(--form-inside-padding-desktop, 16px 32px)"};
  }
`

const StyledReferralFormSection = styled.div`
  padding: 16px;
  ${(p) => p.theme.breakpoints.up("md")} {
    padding: 16px 32px;
  }
`

const StyledAlign = styled.div`
  display: flex;
  align-items: var(--form-align-items, initial);
  justify-content: var(--form-align, center);
  height: var(--form-align-height, auto);
  width: var(--form-align-width, auto);
  position: var(--form-align-position, static);
  z-index: var(--form-align-z-index, auto);
  right: var(--form-align-right, 0px);
  top: var(--form-align-top, auto);
  transform: var(--form-align-transform, none);
  background-color: var(--form-align-bg, transparent);
`

const StyledRecaptchaWrapper = styled.div`
  position: var(--form-recaptcha-position, static);
  z-index: var(--form-recaptcha-z-index, auto);
`

const StyledTitle = styled.div`
  padding: var(--form-title-padding-mobile, 0);
  ${(p) => p.theme.breakpoints.up("md")} {
    padding: var(--form-title-padding-desktop, 0);
  }
`
const StyledButton = styled(Button)`
  display: flex;
  height: 45px;
  justify-content: center;
  align-items: center;
  gap: 8px;
  text-decoration: none;
  font-size: 18px;
  font-weight: 600;
  border-radius: 8px;
  width: var(--form-button-width-mobile, 100%);
  max-width: var(--form-button-max-width-mobile, 328px);
  padding: 8px 16px;
  margin: 0 24px;
  margin-top: var(--form-button-margin-top-mobile, 0);
  border: 1px solid var(--form-button-border, ${(p) => p.theme.colors.buttonActiveBackgroundColor});
  color: var(--form-button-color, ${(p) => p.theme.colors.buttonHoverTextColor});
  background: var(--form-button-bg, ${(p) => p.theme.colors.buttonActiveBackgroundColor});
  :hover {
    text-decoration: none;
    border: 1px solid
      var(--form-button-hover-border, ${(p) => p.theme.colors.buttonActiveBackgroundColor});
    background: var(--form-button-hover-bg, ${(p) => p.theme.colors.buttonActiveBackgroundColor});
    color: var(--form-button-hover-color, ${(p) => p.theme.colors.buttonHoverTextColor});
  }
  &.Mui-disabled {
    color: ${(p) => p.theme.colors.gray};
    border: 1px solid ${(p) => p.theme.colors.lightGrey};
    background: ${(p) => p.theme.colors.lightGrey};
  }
  ${(p) => p.theme.breakpoints.up("md")} {
    width: var(--form-button-width-desktop, var(--form-button-width-mobile, 100%));
    max-width: var(--form-button-max-width-desktop, var(--form-button-max-width-mobile, 328px));
    margin-top: var(--form-button-margin-top-desktop, var(--form-button-margin-top-mobile, 0));
  }
`

const StyledCheckboxContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: flex-start;
`

const StyledCheckbox = styled(Checkbox, {
  shouldForwardProp: (prop) => prop !== "status",
})<{ status: boolean }>`
  padding: 1px 10px 0px 0;
  ${(props) =>
    !props.status
      ? `  rect {
    stroke: red;
  }`
      : ""}
  &.Mui-focusVisible {
    border: 1px dashed white;
    outline-offset: 2px;
    outline: 2px solid black;
  }
`

const StyledModal = styled(Modal)`
  top: unset;
  bottom: 0;
  max-height: calc(100% - 80px);
  overflow: scroll;
  display: flex;
  justify-content: center;
  align-items: flex-end;
  height: 100%;
  ${(props) => props.theme.breakpoints.up("md")} {
    align-items: center;
  }
`
const env = process.env.ENV
const getMainSiteDomain = (): string => {
  if (env === "production") {
    return "https://www.ashtrom.co.il"
  } else if (env === "production_temp") {
    return "https://prod-app-b2b-main.azurewebsites.net"
  } else if (env === "staging") {
    return "https://testmain.ashtrom.co.il"
  } else {
    return "https://www.ashtrom.co.il"
  }
}

const createFileNameSchema = (requireCv: boolean) =>
  requireCv
    ? yup
        .string()
        .required("נא לצרף קובץ קורות חיים")
        .test((value) => value?.trim().length > 0)
    : yup.string().nullable().notRequired()

const buildRegularSchema = (requireCv: boolean) =>
  yup.object({
    firstName: schemaCommon.firstName,
    lastName: schemaCommon.lastName,
    email: schemaCommon.email,
    phone: schemaCommon.allPhoneNumber,
    fileName: createFileNameSchema(requireCv),
  })

const buildEmployeeSchema = (requireCv: boolean) =>
  yup.object({
    firstName: schemaCommon.firstName,
    lastName: schemaCommon.lastName,
    email: schemaCommon.emailNonRequired,
    phone: schemaCommon.allPhoneNumber,
    fileName: createFileNameSchema(requireCv),
    isApprovedTerms: yup
      .boolean()
      .oneOf([true], "עליך לאשר את התנאים כדי להגיש מועמדות")
      .required("עליך לאשר את התנאים כדי להגיש מועמדות"),
    referralName: schemaCommon.firstName,
    referralPhone: schemaCommon.allPhoneNumber,
    referralEmail: schemaCommon.email,
  })

// Shared referral constants – single source of truth in utils/referral.ts
import {
  REFERRAL_QUERY_KEYS,
  REFERRAL_SOURCE_STORAGE_KEY,
  storeReferralSource,
} from "utils/referral"

type RegularSchema = ReturnType<typeof buildRegularSchema>
type EmployeeSchema = ReturnType<typeof buildEmployeeSchema>

export type FormInput =
  | (yup.InferType<RegularSchema> & { isReferral?: false })
  | (yup.InferType<EmployeeSchema> & { isReferral: true })

export type FileState = {
  fileName: string | null
  fileSize: number
  file: string | null
  fileError: string | null
}

export type SubmitResultPayload = {
  state: "success" | "fail"
  isReferral: boolean
}
const FormComponent = ({
  orderId,
  jobType,
  messages,
  isReferral,
  isHot = false,
  requireCv: requireCvProp,
  onValidSubmit,
  onSubmitResult,
  disableSuccessRedirect = false,
}: {
  orderId: number
  jobType?: any
  messages?: any
  setModalOpen?: any
  isReferral: boolean
  isHot?: boolean
  requireCv?: boolean
  onValidSubmit?: () => void
  // eslint-disable-next-line no-unused-vars
  onSubmitResult?: (payload: SubmitResultPayload) => void
  disableSuccessRedirect?: boolean
}) => {
  const router = useRouter()
  const usesExternalResult = Boolean(onSubmitResult)
  const [progress, setProgress] = useState<boolean>(false)
  const [isSubmittedForm, setIsSubmittedForm] = useState(false)
  const [submitState, setSubmitState] = useState<"success" | "fail">("fail")
  const [modalOpen, setModalOpen] = useState(false)
  const [referralSource, setReferralSource] = useState<string>("")
  const t = useTranslations("ProjectsPage")
  const [fileState, setFileState] = useState<FileState>({
    fileName: null,
    fileSize: 0,
    file: null,
    fileError: null,
  })
  const reCaptchaRef = useRef<ReCAPTCHA>()

  const scrollToForm = () => {
    const target = document.getElementById("target-section")
    const top = (target?.offsetTop || 0) - 200
    window.scrollTo({ top, behavior: "smooth" })
  }

  const handleSuccess = () => {
    if (disableSuccessRedirect) {
      setModalOpen(false)
      setIsSubmittedForm(false)
      return
    }

    router.push("/career")
  }

  useEffect(() => {
    if (typeof window === "undefined") return
    const search = new URLSearchParams(window.location.search)
    // Persist any referral code found in the current URL
    storeReferralSource(search)

    const urlCode = REFERRAL_QUERY_KEYS.map((key) => search.get(key)).find((value) => value)
    const stored = sessionStorage.getItem(REFERRAL_SOURCE_STORAGE_KEY) || ""
    const nextValue = (urlCode || stored || "").trim()

    setReferralSource(nextValue)
  }, [])

  const requireCv = requireCvProp !== undefined ? requireCvProp : !isHot
  const regularSchema = useMemo(() => buildRegularSchema(requireCv), [requireCv])
  const employeeSchema = useMemo(() => buildEmployeeSchema(requireCv), [requireCv])

  const {
    control,
    handleSubmit,
    setValue,
    getValues,
    formState: { errors, isSubmitted, isValid },
    reset,
    trigger,
  } = useForm<FormInput>({
    resolver: yupResolver(isReferral ? employeeSchema : regularSchema),
  })

  const focusFirstError = (validationErrors: Record<string, unknown>) => {
    const fieldOrder = isReferral
      ? [
          "firstName",
          "lastName",
          "phone",
          "email",
          "fileName",
          "isApprovedTerms",
          "referralName",
          "referralEmail",
          "referralPhone",
        ]
      : ["firstName", "lastName", "phone", "email", "fileName"]
    const firstErrorField = fieldOrder.find((field) => validationErrors[field])
    if (!firstErrorField) return

    // Wait for React to finish rendering error states before focusing
    requestAnimationFrame(() => {
      if (firstErrorField === "fileName") {
        const label = document.querySelector<HTMLElement>('label[for="cv-file-upload"]')
        label?.focus()
        label?.scrollIntoView({ behavior: "smooth", block: "center" })
      } else if (firstErrorField === "isApprovedTerms") {
        const container = document.getElementById("isApprovedTerms-container")
        container?.focus()
        container?.scrollIntoView({ behavior: "smooth", block: "center" })
      } else {
        const input = document.getElementById(firstErrorField) as HTMLInputElement | null
        input?.focus()
        input?.scrollIntoView({ behavior: "smooth", block: "center" })
      }
    })
  }
  const { mutate } = useMutation({
    mutationKey: ["postForm"],
    mutationFn: postApply,
    onSuccess: () => {
      jobFormSuccessClick("שליחה", jobType, "טופס בעמוד משרה", false)
      if (usesExternalResult) {
        onSubmitResult?.({ state: "success", isReferral })
        reset()
        return
      }
      setSubmitState("success")
      setIsSubmittedForm(true)
      setModalOpen(true)
      reset()
    },
    onError: (error) => {
      jobFormErrorClick("שליחה", jobType, "טופס בעמוד משרה", error.message)
      if (usesExternalResult) {
        onSubmitResult?.({ state: "fail", isReferral })
        reset()
        return
      }
      setSubmitState("fail")
      setIsSubmittedForm(true)
      setModalOpen(true)
      reset()
      scrollToForm()
    },
  })

  useEffect(() => {
    if (messages && messages.checkbox) {
      messages.checkbox = messages.checkbox
        .replace(
          "privacy policy",
          `<a href="${getMainSiteDomain()}/content/privacy-policy" style="color: black">privacy policy</a>`,
        )
        .replace(
          "terms of use",
          `<a href="${getMainSiteDomain()}/content/terms-of-use" style="color: black">terms of use</a>`,
        )

      messages.checkbox = messages.checkbox
        .replace(
          "מדיניות הפרטיות",
          `<a href="${getMainSiteDomain()}/content/privacy-policy" style="color: black">מדיניות הפרטיות</a>`,
        )
        .replace(
          "תנאי השימוש",
          `<a href="${getMainSiteDomain()}/content/terms-of-use" style="color: black">תנאי השימוש</a>`,
        )
    }
  }, [messages])

  useEffect(() => {
    reset({
      firstName: "",
      lastName: "",
      phone: "",
      email: "",
      referralEmail: "",
      referralName: "",
      referralPhone: "",
      isApprovedTerms: false,
      fileName: "",
    })
    setFileState({
      fileName: null,
      fileSize: 0,
      file: null,
      fileError: null,
    })
  }, [isReferral, reset])

  const onSubmit: SubmitHandler<FormInput> = async (data) => {
    const hasFile = !!fileState.file
    const hasFileError = !!fileState.fileError

    if ((requireCv && (!hasFile || hasFileError)) || (!requireCv && hasFileError)) {
      return
    }

    reCaptchaRef?.current?.reset()
    const recaptchaResponse = (await reCaptchaRef?.current?.executeAsync()) as string
    if (!usesExternalResult) {
      setModalOpen(true)
    }
    const storedReferral =
      (typeof window !== "undefined" && sessionStorage.getItem(REFERRAL_SOURCE_STORAGE_KEY)) ||
      referralSource ||
      ""
    const parsedReferral = Number(storedReferral)
    const referralSourceCode = Number.isFinite(parsedReferral) ? parsedReferral : undefined

    const fileBase64 = hasFile && fileState.file ? fileState.file : ""
    const fileNameWithExtension = hasFile && fileState.fileName ? fileState.fileName : ""

    mutate({
      cvFileBase64: fileBase64,
      email: data.email || "",
      fileNameWithExtension,
      firstName: data.firstName,
      isApprovedTerms: true,
      lastName: data.lastName,
      orderId: orderId,
      phone: data.phone,
      gRecaptchaResponse: recaptchaResponse,
      isReferral: isReferral,
      referralEmail: (data as yup.InferType<EmployeeSchema>)?.referralEmail,
      referralName: (data as yup.InferType<EmployeeSchema>)?.referralName,
      referralPhone: (data as yup.InferType<EmployeeSchema>)?.referralPhone,
      ...(referralSourceCode !== undefined && !isReferral ? { referralSourceCode } : {}),
    })
    onValidSubmit?.()
    // Only show upload progress when a CV file is actually present
    setProgress(hasFile)
  }

  const mandatoryText = useMemo(() => {
    if (isReferral) {
      return messages?.mandatoryReferral
        ? messages?.mandatoryReferral
        : "יש למלא את הפרטים שלך כדי שנדע למי לפרגן"
    }
    return messages?.mandatory
      ? messages?.mandatory
      : "פעולה קצרה, שיכולה להוביל להזדמנות גדולה. בואו נכיר"
  }, [isReferral, messages?.mandatory, messages?.mandatoryReferral])

  const attachLabelRaw = isReferral ? messages?.candidateAttach : messages?.attach
  const attachLabel = requireCv ? attachLabelRaw : attachLabelRaw?.replace(/\*/g, "")

  return (
    <StyledForm onSubmit={handleSubmit(onSubmit, focusFirstError)} id="target-section">
      {progress ? (
        <StyledModal open={modalOpen} onClose={() => setModalOpen(false)}>
          <UploadProgress
            filename={fileState.fileName}
            setProgress={setProgress}
            filesize={fileState.fileSize}
            setFileState={setFileState}
          />
        </StyledModal>
      ) : null}
      {!progress && isSubmittedForm ? (
        <StyledModal
          open={modalOpen}
          onClose={() => {
            setModalOpen(false)
            setIsSubmittedForm(false)
          }}
        >
          <SuccessFailJob
            isReferral={isReferral}
            state={submitState}
            setIsSubmitted={setIsSubmittedForm}
            handleSuccess={handleSuccess}
          />
        </StyledModal>
      ) : null}
      {!progress && !isSubmittedForm ? (
        <>
          {!isReferral && (
            <StyledTitle>
              {isReferral && (
                <NewText
                  component="p"
                  desktopFontSize={18}
                  desktopLineHeight="26px"
                  color={colors.black}
                  mb={2}
                >
                  פרטי הממליץ/ה
                </NewText>
              )}
              <NewText
                component="p"
                desktopFontSize={18}
                desktopLineHeight="26px"
                color={colors.black}
              >
                {mandatoryText}
              </NewText>
            </StyledTitle>
          )}
          <StyledInsideForm $withBackground={isReferral}>
            {isReferral && (
              <>
                <Box
                  display={"flex"}
                  gap={"16px"}
                  alignItems={"flex-end"}
                  flexWrap={"wrap"}
                  sx={{
                    marginBottom: "8px",
                    "@media (max-width:600px)": {
                      marginBottom: "16px",
                    },
                  }}
                >
                  <Mobile>
                    <Image src={"/images/CareerLogo.png"} alt="logo" width={61} height={45} />
                  </Mobile>
                  <Desktop>
                    <Image src={"/images/CareerLogo.png"} alt="logo" width={90} height={66} />
                  </Desktop>
                  <NewText
                    component="p"
                    desktopFontSize={20}
                    desktopLineHeight="150%"
                    mobileFontSize={20}
                    mobileLineHeight={"150%"}
                    mobileFontWeight={400}
                    desktopFontWeight={400}
                    color={colors.black}
                    sx={{
                      letterSpacing: {
                        xs: "-0.4px",
                      },
                    }}
                  >
                    מה יותר טוב מלעבוד{" "}
                    <NewText
                      component={"span"}
                      desktopFontSize={20}
                      desktopLineHeight="24px"
                      mobileFontSize={20}
                      mobileLineHeight={"24px"}
                      desktopFontWeight={"700"}
                      color={colors.black}
                      sx={{
                        letterSpacing: {
                          xs: "-0.4px",
                        },
                      }}
                    >
                      בקבוצת אשטרום?
                    </NewText>
                  </NewText>
                </Box>
                <Box
                  sx={{
                    marginBottom: "24px",
                    "@media (max-width:600px)": {
                      marginBottom: "32px",
                    },
                  }}
                >
                  <NewText
                    component="p"
                    desktopFontSize={18}
                    desktopLineHeight="29px"
                    mobileFontSize={18}
                    mobileLineHeight={"23px"}
                    color={colors.black}
                  >
                    לצרף את החברים שלך ולהנות ביחד מכל הטוב הזה! זה המקום להמליץ על חברים – אנחנו
                    בונים על א.נשים מעולים, בדיוק כמוך.
                  </NewText>
                </Box>
              </>
            )}
            {isReferral && (
              <NewText
                component="p"
                desktopFontSize={18}
                desktopLineHeight="26px"
                color={colors.black}
                mb={2}
              >
                פרטי החבר/ה
              </NewText>
            )}
            <Grid container spacing={3} rowSpacing={2}>
              <Grid item xs={12} md={6}>
                <Controller
                  name="firstName"
                  control={control}
                  render={({ field }) => (
                    <FormControl variant="filled" fullWidth>
                      <InputLabel htmlFor="firstName">
                        {isReferral
                          ? messages?.candidateFirstname
                          : messages?.firstname
                          ? messages?.firstname
                          : "שם פרטי*"}
                      </InputLabel>
                      <FilledInput
                        {...field}
                        id="firstName"
                        error={!!errors.firstName}
                        fullWidth
                        aria-describedby="firstName-error"
                      />
                      <StyledHelperText id="firstName-error" status={!errors.firstName}>
                        <StyledTextResponse>
                          {isSubmitted ? (
                            <>
                              <CheckMark2Icon state={!errors.firstName} />
                              <span>
                                {errors.firstName ? errors.firstName.message : t("completed")}
                              </span>
                            </>
                          ) : null}
                        </StyledTextResponse>
                      </StyledHelperText>
                    </FormControl>
                  )}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Controller
                  name="lastName"
                  control={control}
                  render={({ field }) => (
                    <FormControl variant="filled" fullWidth>
                      <InputLabel htmlFor="lastName">
                        {isReferral
                          ? messages?.candidateLastname
                          : messages?.lastname
                          ? messages?.lastname
                          : "שם משפחה*"}
                      </InputLabel>
                      <FilledInput
                        {...field}
                        id="lastName"
                        error={!!errors.lastName}
                        fullWidth
                        aria-describedby="lastName-error"
                      />
                      <StyledHelperText id="lastName-error" status={!errors.lastName}>
                        {isSubmitted ? (
                          <>
                            <StyledTextResponse>
                              <CheckMark2Icon state={!errors.lastName} />
                              <span>
                                {errors.lastName ? errors.lastName.message : t("completed")}
                              </span>
                            </StyledTextResponse>
                          </>
                        ) : null}
                      </StyledHelperText>
                    </FormControl>
                  )}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Controller
                  name="phone"
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <FormControl variant="filled" fullWidth>
                      <InputLabel htmlFor="phone">
                        {isReferral
                          ? messages?.candidatePhone
                          : messages?.phone
                          ? `${messages?.phone}`
                          : `טלפון*`}
                      </InputLabel>
                      <FilledInput
                        {...field}
                        id="phone"
                        error={!!errors.phone}
                        fullWidth
                        aria-describedby="phone-error"
                      />
                      <StyledHelperText id="phone-error" status={!errors.phone}>
                        {isSubmitted ? (
                          <>
                            <StyledTextResponse>
                              <CheckMark2Icon state={!errors.phone} />
                              <span>{errors.phone ? errors.phone.message : t("completed")}</span>
                            </StyledTextResponse>
                          </>
                        ) : null}
                      </StyledHelperText>
                    </FormControl>
                  )}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Controller
                  name="email"
                  control={control}
                  render={({ field }) => (
                    <FormControl variant="filled" fullWidth>
                      <InputLabel htmlFor="email">
                        {isReferral
                          ? messages?.candidateEmail
                          : messages?.email
                          ? messages?.email
                          : "כתובת דוא״ל*"}
                      </InputLabel>
                      <FilledInput
                        {...field}
                        id="email"
                        error={!!errors.email}
                        fullWidth
                        aria-describedby="email-error"
                      />
                      <StyledHelperText id="email-error" status={!errors.email}>
                        {isSubmitted ? (
                          <>
                            <StyledTextResponse>
                              <CheckMark2Icon state={!errors.email} />
                              <span>{errors.email ? errors.email.message : t("completed")}</span>
                            </StyledTextResponse>
                          </>
                        ) : null}
                      </StyledHelperText>
                    </FormControl>
                  )}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FileUpload
                  setValue={setValue}
                  formError={errors.fileName}
                  fileState={fileState}
                  setFileState={setFileState}
                  isSubmitted={isSubmitted}
                  setProgress={setProgress}
                  trigger={trigger}
                  attach={attachLabel}
                  backgroundColor="#FFF"
                />
              </Grid>
              {isReferral && (
                <Grid
                  item
                  xs={12}
                  md={6}
                  sx={{
                    "@media (max-width:600px)": {
                      paddingTop: "52px !important",
                    },
                  }}
                >
                  <StyledCheckboxContainer id="isApprovedTerms-container" tabIndex={-1}>
                    <StyledCheckbox
                      inputProps={{
                        id: "isApprovedTerms",
                        "aria-labelledby": "isApprovedTerms-label",
                        "aria-describedby": "isApprovedTerms-error",
                      }}
                      icon={<CheckboxIcon />}
                      checkedIcon={<CheckedCheckboxIcon color={sharedColors.corpBlue} />}
                      sx={{
                        ":hover": {
                          backgroundColor: "transparent",
                        },
                      }}
                      status={true}
                      value={getValues("isApprovedTerms")}
                      onChange={(e) => {
                        const checked = e.target?.checked
                        setValue("isApprovedTerms", checked, { shouldValidate: true })
                      }}
                    />

                    <NewText
                      id="isApprovedTerms-label"
                      desktopFontSize={14}
                      desktopFontWeight="400"
                      desktopLineHeight="20px"
                      mobileFontSize={12}
                      mobileFontWeight="400"
                      mobileLineHeight={"17px"}
                      component="p"
                      color={"black"}
                      maxWidth={"750px"}
                      dangerouslySetInnerHTML={{ __html: messages.checkbox }}
                    />
                  </StyledCheckboxContainer>
                  <StyledHelperText
                    id="isApprovedTerms-error"
                    status={!(errors as any).isApprovedTerms}
                  >
                    {isSubmitted ? (
                      <>
                        <StyledTextResponse>
                          <CheckMark2Icon state={!(errors as any).isApprovedTerms} />
                          <span>
                            {(errors as any).isApprovedTerms
                              ? (errors as any).isApprovedTerms.message
                              : t("completed")}
                          </span>
                        </StyledTextResponse>
                      </>
                    ) : null}
                  </StyledHelperText>
                </Grid>
              )}
            </Grid>
          </StyledInsideForm>
          {isReferral && (
            <StyledTitle>
              {isReferral && (
                <NewText
                  component="p"
                  desktopFontSize={18}
                  desktopLineHeight="26px"
                  color={colors.black}
                  mb={2}
                >
                  פרטי הממליץ/ה
                </NewText>
              )}
              <NewText
                component="p"
                desktopFontSize={18}
                desktopLineHeight="26px"
                color={colors.black}
              >
                {mandatoryText}
              </NewText>
            </StyledTitle>
          )}
          {isReferral && (
            <StyledReferralFormSection>
              <Grid container spacing={3} rowSpacing={2}>
                <Grid item xs={12} md={6}>
                  <Controller
                    name="referralName"
                    control={control}
                    render={({ field }) => (
                      <FormControl variant="filled" fullWidth>
                        <InputLabel htmlFor="referralName">{messages?.referralName}</InputLabel>
                        <FilledInput
                          {...field}
                          id="referralName"
                          error={!!(errors as any).referralName}
                          fullWidth
                          aria-describedby="referralName-error"
                        />
                        <StyledHelperText
                          id="referralName-error"
                          status={!(errors as any).referralName}
                        >
                          <StyledTextResponse>
                            {isSubmitted ? (
                              <>
                                <CheckMark2Icon state={!(errors as any).referralName} />
                                <span>
                                  {(errors as any).referralName
                                    ? (errors as any).referralName.message
                                    : t("completed")}
                                </span>
                              </>
                            ) : null}
                          </StyledTextResponse>
                        </StyledHelperText>
                      </FormControl>
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Controller
                    name="referralEmail"
                    control={control}
                    render={({ field }) => (
                      <FormControl variant="filled" fullWidth>
                        <InputLabel htmlFor="referralEmail">
                          {messages?.referralEmail || "כתובת דוא״ל"}
                        </InputLabel>
                        <FilledInput
                          {...field}
                          id="referralEmail"
                          error={!!(errors as any).referralEmail}
                          fullWidth
                          aria-describedby="referralEmail-error"
                        />
                        <StyledHelperText
                          id="referralEmail-error"
                          status={!(errors as any).referralEmail}
                        >
                          {isSubmitted ? (
                            <>
                              <StyledTextResponse>
                                <CheckMark2Icon state={!(errors as any).referralEmail} />
                                <span>
                                  {(errors as any).referralEmail
                                    ? (errors as any).referralEmail.message
                                    : t("completed")}
                                </span>
                              </StyledTextResponse>
                            </>
                          ) : null}
                        </StyledHelperText>
                      </FormControl>
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Controller
                    name="referralPhone"
                    control={control}
                    defaultValue=""
                    render={({ field }) => (
                      <FormControl variant="filled" fullWidth>
                        <InputLabel htmlFor="referralPhone">
                          {messages?.referralPhone || `טלפון*`}
                        </InputLabel>
                        <FilledInput
                          {...field}
                          id="referralPhone"
                          error={!!(errors as any).referralPhone}
                          fullWidth
                          aria-describedby="referralPhone-error"
                        />
                        <StyledHelperText
                          id="referralPhone-error"
                          status={!(errors as any).referralPhone}
                        >
                          {isSubmitted ? (
                            <>
                              <StyledTextResponse>
                                <CheckMark2Icon state={!(errors as any).referralPhone} />
                                <span>
                                  {(errors as any).referralPhone
                                    ? (errors as any).referralPhone.message
                                    : t("completed")}
                                </span>
                              </StyledTextResponse>
                            </>
                          ) : null}
                        </StyledHelperText>
                      </FormControl>
                    )}
                  />
                </Grid>
              </Grid>
            </StyledReferralFormSection>
          )}
          <Box
            sx={{
              padding: "32px",
              "@media (max-width:600px)": {
                padding: "30px 24px",
              },
            }}
          >
            {isReferral && (
              <NewText
                tabIndex={0}
                desktopFontSize={14}
                desktopFontWeight="400"
                desktopLineHeight="20px"
                mobileFontWeight="400"
                component="p"
                color={"black"}
                maxWidth={"750px"}
              >
                *אין להעביר קורות חיים או פרטים אודות חבר/ה ללא ידיעתם
              </NewText>
            )}
            <NewText
              desktopFontSize={14}
              desktopFontWeight="400"
              desktopLineHeight="20px"
              mobileFontWeight="400"
              component="p"
              color={"black"}
              maxWidth={"750px"}
              dangerouslySetInnerHTML={{
                __html: `${isReferral ? "*" : ""}${ctaMessage(
                  t(isReferral ? "ctaMessageForRecommendAFriend" : "ctaMessage") || "",
                )}`,
              }}
            />
          </Box>
          <StyledAlign>
            <StyledButton
              type="submit"
              onClick={() => {
                jobFormClick("שליחה", jobType, "טופס בעמוד משרה")

                Object.keys(errors).length > 0 ||
                  (!isValid &&
                    jobFormErrorClick(
                      "שליחה",
                      jobType,
                      "טופס בעמוד משרה",
                      errors?.email?.message ||
                        errors?.fileName?.message ||
                        errors?.firstName?.message ||
                        errors?.lastName?.message ||
                        errors?.phone?.message ||
                        "Fields are mandatory",
                    ))
              }}
              disabled={isSubmitted && (Object.keys(errors).length > 0 || !isValid)}
            >
              {messages?.send ? messages?.send : "שליחה"}
            </StyledButton>
          </StyledAlign>
        </>
      ) : null}
      {RECAPTCHA_KEY && (
        <StyledRecaptchaWrapper>
          <ReCAPTCHA
            ref={reCaptchaRef as LegacyRef<ReCAPTCHA> | undefined}
            sitekey={RECAPTCHA_KEY}
            size="invisible"
            hl="iw"
            tabIndex={-1}
          />
        </StyledRecaptchaWrapper>
      )}
    </StyledForm>
  )
}

export default FormComponent
